import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';

import { MatDialog } from '@angular/material/dialog';
import { HttpService } from '../../core/services/http/http.service';
import {
  GetAllListSeasonal,
  SetActualBaseSeasonal,
  SetActualFiltersSeasonal,
  SetDetailDataSeasonal,
  SetEndDateSeasonal,
  SetExcludePUDCItemsSeasonal,
  SetExcludeRCTDACItemsSeasonal,
  SetExcludeRCUDCItemsSeasonal,
  SetIgnoredDatesSeasonal,
  SetInitialDateSeasonal,
  SetOptionSelected,
  SetRowDataSeasonal,
  SetYearUncheckedOptionSelected,
  UpdateModifiedDataSeasonal,
  SetIdOptionSelect,
  SetResetState,
  SetSeasonalOption,
  GetSeasonalDataDetails,
  SetSeasonalMedidorId,
  ClearOptionSelected,
} from './seasonal-prediction.action';
import { EnovaAlertsComponent } from '../../core/utils/enova-alerts/enova-alerts.component';
import { PredictionService } from '@/app/core/services/prediction/prediction.service';
export interface SeasonalPredictionStateModel {
  modifiedDataSeasonal: { [key: string]: number };
  rowDataSeasonal: any[];
  seasonalListOptions: any[];
  actualBaseSeasonal: number;
  filters: { [key: string]: any };
  startDateSeasonal: string;
  endDateSeasonal: string;
  ignoredDates: any[];
  excludePUDCItems: any[];
  excludeRCTDACItems: any[];
  excludeRCUDCItems: any[];
  optionSelected: string;
  detailDataSeasonal: {};
  yearUncheckedOption: {
    ['udc']: [];
    ['pudc']: [];
    ['rcdtac']: [];
    ['rcudc']: [];
  };
  graphSeasonalData: {
    labels: any[];
    datasets: any[];
  };
  idOptionSelect: string;
  seasonalOption: string;
  medidorId: number | null;
}

const defaultState: SeasonalPredictionStateModel = {
  modifiedDataSeasonal: {
    'Último dato coincidente': 100,
    'Promedio de los últimos datos coincidentes': 0,
    'Razón de cambio de un día coincidente respecto a un periodo típico anterior': 0,
    'Razón de cambio respecto a los últimos datos coincidentes': 0,
  },
  rowDataSeasonal: [],
  seasonalListOptions: [],
  actualBaseSeasonal: 3,
  filters: {},
  startDateSeasonal: '',
  endDateSeasonal: '',
  ignoredDates: [],
  excludePUDCItems: [],
  excludeRCTDACItems: [],
  excludeRCUDCItems: [],
  optionSelected: '',
  detailDataSeasonal: {},
  yearUncheckedOption: {
    udc: [],
    pudc: [],
    rcdtac: [],
    rcudc: [],
  },
  graphSeasonalData: {
    labels: [],
    datasets: [],
  },
  idOptionSelect: '',
  seasonalOption: '',
  medidorId: null,
};

@State<SeasonalPredictionStateModel>({
  name: 'modifiedDataSeasonal',
  defaults: defaultState,
})
@Injectable()
export class SeasonalPredictionState {
  @Selector()
  static getModifiedDataSeasonal(state: SeasonalPredictionStateModel) {
    return state.modifiedDataSeasonal;
  }

  @Selector()
  static getRowDataSeasonal(state: SeasonalPredictionStateModel) {
    return state.rowDataSeasonal;
  }

  @Selector()
  static getSeasonalListOptions(state: SeasonalPredictionStateModel) {
    return state.seasonalListOptions;
  }

  @Selector()
  static getActualBase(state: SeasonalPredictionStateModel) {
    return state.actualBaseSeasonal;
  }

  @Selector()
  static getFilters(state: SeasonalPredictionStateModel) {
    return state.filters;
  }

  @Selector()
  static getStartDate(state: SeasonalPredictionStateModel) {
    return state.startDateSeasonal;
  }

  @Selector()
  static getEndDate(state: SeasonalPredictionStateModel) {
    return state.endDateSeasonal;
  }

  @Selector()
  static getIgnoredDate(state: SeasonalPredictionStateModel) {
    return state.ignoredDates;
  }

  @Selector()
  static getExcludePUDCItems(state: SeasonalPredictionStateModel) {
    return state.excludePUDCItems;
  }

  @Selector()
  static getExcludeRCTDACItems(state: SeasonalPredictionStateModel) {
    return state.excludeRCTDACItems;
  }

  @Selector()
  static getExcludeRCUDCItems(state: SeasonalPredictionStateModel) {
    return state.excludeRCUDCItems;
  }

  @Selector()
  static getOptionSelected(state: SeasonalPredictionStateModel) {
    return state.optionSelected;
  }

  @Selector()
  static getDetailData(state: SeasonalPredictionStateModel) {
    return state.detailDataSeasonal;
  }

  @Selector()
  static getYearUncheckedOption(state: SeasonalPredictionStateModel) {
    return state.yearUncheckedOption;
  }

  @Selector()
  static getIdOptionSelect(state: SeasonalPredictionStateModel) {
    return state.idOptionSelect;
  }

  @Selector()
  static getSeasonalOption(state: SeasonalPredictionStateModel) {
    return state.seasonalOption;
  }

  @Selector()
  static getDataGraphSeasonal(state: SeasonalPredictionStateModel) {
    return state.graphSeasonalData;
  }

  @Selector()
  static getMedidorId(state: SeasonalPredictionStateModel) {
    return state.medidorId;
  }

  constructor(
    private httpService: HttpService,
    public dialog: MatDialog,
    private predictionService: PredictionService
  ) {}

  @Action(GetSeasonalDataDetails)
  async GetSeasonalDataDetails(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: GetSeasonalDataDetails
  ) {
    const { startDate, endDate, uuid, filters } = action.payload;

    try {
      const response = await this.predictionService.getDataSeasonalPrediction(
        startDate,
        endDate,
        uuid,
        filters
      );

      if (response) {
        const graphData =
          await this.predictionService.getGrapDataPrediction(response);

        if (graphData) {
          const datasets = [
            {
              label: 'UDC',
              data: graphData?.data['udc']?.map(row => row.valor),
              borderColor: '#ED7D31',
            },
            {
              label: 'PUDC',
              data: graphData?.data['pudc']?.map(row => row.valor),
              borderColor: '#A5A5A5',
              borderDash: [10, 5],
            },
            {
              label: 'RCDTAC',
              data: graphData?.data['rcdtac']?.map(row => row.valor),
              borderColor: '#FFC000',
              borderDash: [10, 5],
            },
            {
              label: 'RCUDC',
              data: graphData?.data['rcudc']?.map(row => row.valor),
              borderColor: '#5B9BD5',
            },
            {
              label: 'MPP',
              data: graphData?.data['mpp']?.map(row => row.valor),
              borderColor: '#000000',
            },
          ];
          ctx.patchState({
            graphSeasonalData: {
              labels: graphData.labels,
              datasets: datasets,
            },
          });
        }

        ctx.patchState({
          rowDataSeasonal: response,
        });
      }
    } catch (error) {}
  }

  @Action(UpdateModifiedDataSeasonal)
  updateModifiedDataSeasonal(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: UpdateModifiedDataSeasonal
  ) {
    const state = ctx.getState();
    const { key, value } = action.payload;

    const modifiedDataSeasonal = {
      ...state.modifiedDataSeasonal,
      [key]: value,
    };

    ctx.patchState({ modifiedDataSeasonal });
  }

  @Action(SetRowDataSeasonal)
  setRowDataSeasonal(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetRowDataSeasonal
  ) {
    ctx.patchState({
      rowDataSeasonal: action.payload,
    });
  }

  @Action(GetAllListSeasonal)
  async GetAllListSeasonal(ctx: StateContext<SeasonalPredictionStateModel>) {
    try {
      const response = await this.httpService.getAllSeasonalList();
      response.push({
        uuid: '1dd9464e-d0a8-435b-8a21-3c5010c3098b',
        nombre: 'Vacaciones',
        año: '2024',
        fechaInicio: '2024-06-14',
        fechaFin: '2024-07-09',
      });
      ctx.patchState({
        seasonalListOptions: response,
      });
    } catch (error) {
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          hiddeCancelBtn: true,
        },
      });
    }
  }

  @Action(SetActualBaseSeasonal)
  setActualBase(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetActualBaseSeasonal
  ) {
    ctx.patchState({
      actualBaseSeasonal: action.payload,
    });
  }

  @Action(SetActualFiltersSeasonal)
  SetActualFilters(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetActualFiltersSeasonal
  ) {
    ctx.patchState({
      filters: action.payload,
    });
  }

  @Action(SetInitialDateSeasonal)
  SetInitialDate(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetInitialDateSeasonal
  ) {
    ctx.patchState({
      startDateSeasonal: action.payload,
    });
  }

  @Action(SetEndDateSeasonal)
  SetEndDate(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetEndDateSeasonal
  ) {
    ctx.patchState({
      endDateSeasonal: action.payload,
    });
  }

  @Action(SetIgnoredDatesSeasonal)
  SetIgnoredDates(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetIgnoredDatesSeasonal
  ) {
    const prevState = ctx.getState();

    ctx.patchState({
      ignoredDates: action.payload,
    });
    const newState = ctx.getState();
  }

  @Action(SetExcludePUDCItemsSeasonal)
  SetExcludePUDCItems(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetExcludePUDCItemsSeasonal
  ) {
    ctx.patchState({
      excludePUDCItems: action.payload,
    });
  }
  @Action(SetExcludeRCTDACItemsSeasonal)
  SetExcludeRCTDACItems(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetExcludeRCTDACItemsSeasonal
  ) {
    ctx.patchState({
      excludeRCTDACItems: action.payload,
    });
  }
  @Action(SetExcludeRCUDCItemsSeasonal)
  SetExcludeRCUDCItems(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetExcludeRCUDCItemsSeasonal
  ) {
    ctx.patchState({
      excludeRCUDCItems: action.payload,
    });
  }

  @Action(SetOptionSelected)
  SetOptionSelected(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetOptionSelected
  ) {
    ctx.patchState({
      optionSelected: action.payload,
    });
  }

  @Action(SetDetailDataSeasonal)
  SetDetailDataSeasonal(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetDetailDataSeasonal
  ) {
    ctx.patchState({
      detailDataSeasonal: action.payload,
    });
  }

  @Action(SetYearUncheckedOptionSelected)
  SetYearUncheckedOptionSelected(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetYearUncheckedOptionSelected
  ) {
    ctx.patchState({
      yearUncheckedOption: action.payload,
    });
  }
  @Action(SetIdOptionSelect)
  SetIdOptionSelect(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetIdOptionSelect
  ) {
    ctx.patchState({
      idOptionSelect: action.payload,
    });
  }
  @Action(SetResetState)
  SetResetState(ctx: StateContext<SeasonalPredictionStateModel>) {
    ctx.patchState({
      modifiedDataSeasonal: {
        'Último dato coincidente': 100,
        'Promedio de los últimos datos coincidentes': 0,
        'Razón de cambio de un día coincidente respecto a un periodo típico anterior': 0,
        'Razón de cambio respecto a los últimos datos coincidentes': 0,
      },
      rowDataSeasonal: [],
      actualBaseSeasonal: 3,
      filters: {},
      ignoredDates: [],
      excludePUDCItems: [],
      excludeRCTDACItems: [],
      excludeRCUDCItems: [],
      detailDataSeasonal: {},
      yearUncheckedOption: {
        udc: [],
        pudc: [],
        rcdtac: [],
        rcudc: [],
      },
      idOptionSelect: '',
      graphSeasonalData: {
        labels: [],
        datasets: [],
      },
    });
  }
  @Action(SetSeasonalOption)
  SetSeasonalOption(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetSeasonalOption
  ) {
    ctx.patchState({
      seasonalOption: action.payload,
    });
  }

  @Action(SetSeasonalMedidorId)
  SetSeasonalMedidorId(
    ctx: StateContext<SeasonalPredictionStateModel>,
    action: SetSeasonalMedidorId
  ) {
    ctx.patchState({
      medidorId: action.medidor,
    });
  }

  @Action(ClearOptionSelected)
  ClearOptionSelected(ctx: StateContext<SeasonalPredictionStateModel>) {
    ctx.patchState({
      optionSelected: '',
    });
  }
}
